/**
 * Main Dashboard Page
 * This page is intended to allow the customer to view
 * a single day's worth of data from any of their locations.
 */
import { useEffect, useState } from 'react';
import PageLayout from '../../components/ui/PageLayout';
import DropdownFranchise from '../../components/dropdowns/DropdownFranchise';
import DropdownLocationMultiple from '../../components/dropdowns/DropdownLocationMultiple';
import Datepicker from '../../components/time/Datepicker';
import DriveThruLobbyToggle from '../../components/buttons/DriveThruLobbyToggle';
import DayPartsTable from '../../components/tables/DayPartsTable';
import RanksTable from '../../components/tables/RanksTable';
import StationsTable from '../../components/tables/StationsTable';
import DetectionTable from '../../components/tables/DetectionsTable';
import { useData } from '../../hooks/useData';
import ModalBasic from '@/components/modals/ModalBasic';
import GroupModal from '@/components/modals/GroupModal';
import { getAllGroups, getGroupLocationsByName } from '@/utils/api';
import { type GroupResponse } from '@/types/GroupResponse';
import GroupsButton from '@/components/buttons/GroupsButton';
import HellometerLoading from './HellometerLoading';

function Dashboard(): JSX.Element {
  const {
    selectedDates,
    handleSelectedDates,
    selectedFranchise,
    handleSelectGroups,
    cachedGroups,
    selectedLocations,
    loadingProfile
  } = useData();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [groupOptions, setGroupOptions] = useState<GroupResponse[]>([]);

  useEffect(() => {
    const franchiseId = selectedFranchise?.id || '';
    if (cachedGroups.length === 0 || !franchiseId) {
      return;
    }
    // Fetch group locations
    Promise.all(
      cachedGroups.map(async (group) => await getGroupLocationsByName(group, franchiseId))
    ).then((responses) => {
      // Flatten all groupLocations arrays (which are arrays of location IDs)
      const allLocationIds = responses.flat();
      // Remove duplicates
      const uniqueLocationIds = Array.from(new Set(allLocationIds));
      // Map IDs to location objects from selectedLocations (or another source if needed)
      handleSelectGroups(cachedGroups, uniqueLocationIds, '');
    }).catch((error) => {
      console.error(error);
    });
  }, [cachedGroups]);

  useEffect(() => {
    const franchiseId = selectedFranchise?.id || '';
    if (!franchiseId || !selectedFranchise) {
      return;
    }
    getAllGroups(franchiseId)
    .then((res) => { setGroupOptions(res); });
  }, [selectedFranchise]);

  const handleRefreshGroups = () => {
    if (selectedFranchise) {
      getAllGroups(selectedFranchise.id)
      .then((res) => { setGroupOptions(res); });
    }
  };

  return (
    <PageLayout>
      <div data-cy="dashboard">
        {/* Dashboard actions */}
        <div className="sm:flex sm:justify-between sm:items-center mb-8 gap-4">
          {/* Left: Franchise and Location Selector */}
          <div className="flex flex-wrap justify-start gap-2 mb-4">
            {/* Franchise Selector */}
            <DropdownFranchise />
            {/* Location Selector */}
            <DropdownLocationMultiple 
              dataCy="dashboard-location-selector"
              disabled={false}
              />
            {/* Group Filters -- hide if no group options */}
            {groupOptions.length === 0
            ? <></>
            : (
            <>
              <GroupsButton onClick={() => { setIsOpen(prev => !prev); }} />
              <div hidden={true}>
                <ModalBasic
                  title='Groups'
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  dataCy='modal-filter-groups'
                  full_h={true}
                >
                  <GroupModal
                    groupOptions={groupOptions}

                    refreshGroups={() => { handleRefreshGroups(); }}
                  />
                </ModalBasic>
              </div>
            </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
            {/* Datepicker built with flatpickr */}
            <Datepicker
              initDates={selectedDates}
              handleDateChange={handleSelectedDates}
              mode="single"
            />
            {/* Toggles for LOBBY and DT */}
            <DriveThruLobbyToggle />
          </div>
        </div>
        {/* Tables */}
        {(selectedLocations.length === 0 || selectedFranchise === null || loadingProfile)
            ? 
              <HellometerLoading/>
            : <div className="flex flex-col gap-4">
          <RanksTable />
          <DayPartsTable />
          <StationsTable />
          <DetectionTable />
        </div>
        }
        
      </div>
    </PageLayout>
  );
}
export default Dashboard;
